import React from 'react';
import { makeStyles } from 'tss-react/mui';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircle from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { TextField, TextFieldProps } from 'mui-rff';
import { UserProfile } from '../../services/generated/app-api';
import { userDetailsFormValuesFormSchema } from './UserDetailsForm';

interface UserDetailsFromFieldsProps {
    user: UserProfile;
}
const useStyles = makeStyles()(() => ({
    profilePic: {
        width: '100px',
        height: '100px',
        color: 'purple',
        fontSize: 'larger',
        fontWeight: 'bold',
        borderColor: 'purple',
    },
}));
const UserDetailsFromFields: React.FC<UserDetailsFromFieldsProps> = ({ user }) => {
    const { classes } = useStyles();
    const textField: Partial<TextFieldProps> = {
        fullWidth: true,
        variant: 'outlined',
        margin: 'normal',
        disabled: false,
        fieldProps: {
            validateFields: [],
        },
    };
    const isSocialLogin = !user?.userId?.startsWith('auth0');
    const initial = user?.firstName?.charAt(0);
    return (
        <>
            <Grid container spacing={2}>
                <Grid
                    size={{
                        sm: 6,
                        xs: 12,
                    }}
                >
                    <Grid
                        container
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Avatar alt={initial} src={user.picture} className={classes.profilePic} />
                    </Grid>
                </Grid>
                <Grid
                    size={{
                        sm: 6,
                        xs: 12,
                    }}
                >
                    <TextField
                        {...textField}
                        {...userDetailsFormValuesFormSchema.fieldProps.emailAddress}
                        disabled={true}
                        autoComplete="email"
                        helperText="Email address may not be changed"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <TextField
                        {...userDetailsFormValuesFormSchema.fieldProps.emailAddress}
                        id="username"
                        style={{ display: 'none' }}
                        type="text"
                        slotProps={{
                            htmlInput: {
                                name: 'username',
                            },
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid
                    size={{
                        sm: 6,
                        xs: 12,
                    }}
                >
                    <TextField
                        {...textField}
                        disabled={isSocialLogin}
                        {...userDetailsFormValuesFormSchema.fieldProps.givenName}
                        autoComplete="given-name"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountCircle />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
                <Grid
                    size={{
                        sm: 6,
                        xs: 12,
                    }}
                >
                    <TextField
                        {...textField}
                        {...userDetailsFormValuesFormSchema.fieldProps.familyName}
                        disabled={isSocialLogin}
                        autoComplete="family-name"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountCircle />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid
                    hidden={isSocialLogin}
                    size={{
                        sm: 6,
                        xs: 12,
                    }}
                >
                    <TextField
                        {...textField}
                        {...userDetailsFormValuesFormSchema.fieldProps.password}
                        type="password"
                        autoComplete="new-password"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                <Grid
                    hidden={isSocialLogin}
                    size={{
                        sm: 6,
                        xs: 12,
                    }}
                >
                    <TextField
                        {...textField}
                        {...userDetailsFormValuesFormSchema.fieldProps.passwordConfirmation}
                        type="password"
                        autoComplete="new-password"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
            </Grid>
            <List disablePadding>
                <ListItem>
                    <Box
                        sx={{
                            mt: 1,
                            mb: 1,
                        }}
                    >
                        <Divider
                            sx={{
                                opacity: '0.6',
                            }}
                        />
                    </Box>
                </ListItem>
                {isSocialLogin ? null : (
                    <ListItem>
                        <ListItemIcon>
                            <LockIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Last password change"
                            secondary={user.lastPasswordChange ? new Date(user.lastPasswordChange).toLocaleDateString() : 'Never changed'}
                        />
                    </ListItem>
                )}
                <ListItem>
                    <ListItemIcon>
                        <AccessTimeIcon />
                    </ListItemIcon>
                    <ListItemText primary="Last signed in" secondary={new Date(user.lastSignIn).toLocaleDateString()} />
                </ListItem>
            </List>
        </>
    );
};

export default UserDetailsFromFields;
